import { getAmplitudeExperimentVariant, preloadAmplitudeExperiments } from "@/telemetry/amplitude"
import { queryClient } from "@/api/constants"
import type { GetUserResponseBody } from "@/api/dto/users"
import { AppExperimentVariant } from "@/api/dto/users"

const EXPERIMENT_OVERRIDE_PREFIX = "experiment_override_"
const EXPERIMENT_OVERRIDE_CHANGED_EVENT = "experiment-override-changed"

export interface ExperimentDefinition<TVariant extends string = string> {
	key: string
	title: string
	variants: readonly TVariant[]
	fallback: TVariant
}

export const AI_STYLE_ENTRY_POINT_EXPERIMENT = {
	key: "ai-style-entry-point",
	title: "AI Style Entry Point",
	variants: ["control", "treatment"],
	fallback: "control",
} as const satisfies ExperimentDefinition<"control" | "treatment">

export const FOLDER_SELECTION_GUIDANCE_EXPERIMENT = {
	key: "folder-selection-guidance",
	title: "Folder Selection Guidance",
	variants: ["control", "treatment"],
	fallback: "control",
} as const satisfies ExperimentDefinition<"control" | "treatment">

export const EXPERIMENTS: readonly ExperimentDefinition[] = [
	AI_STYLE_ENTRY_POINT_EXPERIMENT,
	FOLDER_SELECTION_GUIDANCE_EXPERIMENT,
]

export const CREATE_NEW_PROJECT_CARD_EXPERIMENT = {
	key: "create-new-project-card",
	title: "Create New Project Card",
	variants: [AppExperimentVariant.Off, AppExperimentVariant.On],
	fallback: AppExperimentVariant.Off,
} as const satisfies ExperimentDefinition<AppExperimentVariant>

const getExperimentOverrideKey = (key: string) => {
	return `${EXPERIMENT_OVERRIDE_PREFIX}${key}`
}

const notifyExperimentVariantOverrideChanged = (key: string) => {
	if (typeof window === "undefined") return
	window.dispatchEvent(new CustomEvent(EXPERIMENT_OVERRIDE_CHANGED_EVENT, { detail: key }))
}

const isExperimentVariant = <TVariant extends string>(
	experiment: ExperimentDefinition<TVariant>,
	value: string | null,
): value is TVariant => {
	return value !== null && experiment.variants.some((variant) => variant === value)
}

export const getExperimentVariantOverride = <TVariant extends string>(
	experiment: ExperimentDefinition<TVariant>,
): TVariant | null => {
	try {
		if (typeof localStorage === "undefined") return null
		const value = localStorage.getItem(getExperimentOverrideKey(experiment.key))
		return isExperimentVariant(experiment, value) ? value : null
	} catch {
		return null
	}
}

export const setExperimentVariantOverride = <TVariant extends string>(
	experiment: ExperimentDefinition<TVariant>,
	variant: TVariant | null,
) => {
	try {
		if (typeof localStorage === "undefined") return
		const key = getExperimentOverrideKey(experiment.key)
		if (variant === null) {
			localStorage.removeItem(key)
			notifyExperimentVariantOverrideChanged(experiment.key)
			return
		}

		localStorage.setItem(key, variant)
		notifyExperimentVariantOverrideChanged(experiment.key)
	} catch {
		// ignore
	}
}

export const subscribeExperimentVariantOverride = (
	experiment: ExperimentDefinition,
	onChange: () => void,
) => {
	if (typeof window === "undefined") return () => {}

	const handleChange = (event: Event) => {
		if (event instanceof CustomEvent && event.detail === experiment.key) {
			onChange()
		}
	}
	window.addEventListener(EXPERIMENT_OVERRIDE_CHANGED_EVENT, handleChange)
	return () => window.removeEventListener(EXPERIMENT_OVERRIDE_CHANGED_EVENT, handleChange)
}

export const preloadExperiments = async (): Promise<void> => {
	if (EXPERIMENTS.every((experiment) => getExperimentVariantOverride(experiment) !== null)) {
		return
	}

	const user = queryClient.getQueryData<GetUserResponseBody>(["user"])
	if (user) {
		await preloadAmplitudeExperiments(user.userId)
	}
}

export const getExperimentVariant = async <TVariant extends string>(
	experiment: ExperimentDefinition<TVariant>,
): Promise<TVariant> => {
	const override = getExperimentVariantOverride(experiment)
	if (override !== null) {
		return override
	}

	const user = queryClient.getQueryData<GetUserResponseBody>(["user"])
	if (!user) {
		return experiment.fallback
	}

	try {
		const variant = await getAmplitudeExperimentVariant(
			experiment.key,
			user.userId,
			experiment.fallback,
		)
		return isExperimentVariant(experiment, variant) ? variant : experiment.fallback
	} catch {
		return experiment.fallback
	}
}
